import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../config/apiConfig";

export default function SearchBar({
  onSelect,
  initialValue = "",
  placeholder = "Search S&P 500…",
  autoFocus = false,
}) {
  const [value, setValue] = useState(initialValue);
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const boxRef = useRef(null);

  // add this helper near the top (below imports)
function mergeUniqueSymbolLists(...lists) {
  const bySym = new Map();
  lists.flat().forEach(r => {
    if (!r || !r.symbol) return;
    const sym = String(r.symbol).toUpperCase();
    const name = r.name || r.companyName || "";
    if (!bySym.has(sym)) {
      bySym.set(sym, { symbol: sym, name });
    } else if (!bySym.get(sym).name && name) {
      bySym.set(sym, { symbol: sym, name });
    }
  });
  return Array.from(bySym.values()).sort((a, b) => a.symbol.localeCompare(b.symbol));
}

// ...then replace your existing useEffect that fetched /api/sp500 or /api/tickers with this:
useEffect(() => {
  let mounted = true;
  setIsLoading(true);
  (async () => {
    try {
      const [nyseTsx, sp500] = await Promise.all([
        axios.get(`${API_BASE_URL}/tickers?ex=NYSE,NASDAQ,AMEX,TSX`),
        axios.get(`${API_BASE_URL}/sp500`),
      ]);
      if (mounted) {
        const merged = mergeUniqueSymbolLists(nyseTsx.data || [], sp500.data || []);
        setOptions(merged);
      }
    } catch (err) {
      console.error('[SearchBar] Error fetching tickers:', err);
      if (mounted) setOptions([]);
    } finally {
      if (mounted) setIsLoading(false);
    }
  })();
  return () => { mounted = false; };
}, []);


  useEffect(() => setHighlight(0), [value, open]);

  useEffect(() => {
    if (!autoFocus) return;
    const t = setTimeout(() => {
      boxRef.current?.querySelector("input")?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [autoFocus]);

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options.slice(0, 20);
    return options
      .filter(o => o.symbol.toLowerCase().includes(q) || (o.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [value, options]);

  const pick = (idx) => {
    const item = filtered[idx];
    if (!item) return;
    setValue(item.symbol);
    setOpen(false);
    onSelect?.(item);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp")) { setOpen(true); return; }
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, Math.max(filtered.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filtered.length) pick(highlight);
      else if (value.trim()) onSelect?.({ symbol: value.trim().toUpperCase(), name: "" });
    } else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <input
        value={value}
        onChange={(e) => { setValue(e.target.value); setOpen(true); }}
        onFocus={(e) => {
          setOpen(true);
          e.currentTarget.style.borderColor = "#6366f1";
          e.currentTarget.style.boxShadow = "0 0 0 3px rgba(99, 102, 241, 0.1)";
        }}
        onBlur={(e) => {
          setTimeout(() => setOpen(false), 120);
          e.currentTarget.style.borderColor = "#2a2a3a";
          e.currentTarget.style.boxShadow = "none";
        }}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        style={{
          width: "100%", 
          padding: window.innerWidth <= 768 ? "14px 16px" : "16px 20px", 
          borderRadius: window.innerWidth <= 768 ? 12 : 16,
          border: "2px solid #2a2a3a", 
          outline: "none", 
          fontSize: window.innerWidth <= 768 ? 16 : 16,
          background: "#1a1a24",
          color: "#e8e8f0",
          transition: "all 0.2s ease",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.2)",
          minHeight: "48px", // Touch-friendly
        }}
      />
      {open && (
        <div
          style={{
            position: "absolute", 
            inset: "calc(100% + 8px) 0 auto 0",
            background: "#1a1a24", 
            border: "1px solid #2a2a3a",
            borderRadius: window.innerWidth <= 768 ? 12 : 16, 
            boxShadow: "0 12px 40px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(99, 102, 241, 0.1)",
            maxHeight: window.innerWidth <= 768 ? 280 : 360, 
            overflowY: "auto", 
            zIndex: 10000,
            backdropFilter: "blur(20px)",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {isLoading ? (
            <div style={{ padding: 20, fontSize: 14, color: "#888898", textAlign: "center" }}>Loading…</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 16, fontSize: 14, color: "#888898", textAlign: "center" }}>No matches</div>
          ) : filtered.map((opt, i) => (
            <div
              key={opt.symbol}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(i)}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "14px 20px", 
                cursor: "pointer",
                background: i === highlight ? "rgba(99, 102, 241, 0.15)" : "transparent",
                display: "flex", 
                alignItems: "baseline", 
                gap: 12,
                transition: "all 0.15s ease",
                borderLeft: i === highlight ? "3px solid #6366f1" : "3px solid transparent",
              }}
            >
              <strong style={{ minWidth: 80, color: "#e8e8f0", fontWeight: 600 }}>{opt.symbol}</strong>
              <span style={{ color: "#b8b8c8", fontSize: "14px" }}>{opt.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
